import { uvToSphereGlsl, sphereToUvGlsl } from './glslUtils';

/** Composite shader — blends a stroke/layer render-target onto a base texture.
 *
 *  Modes:
 *  - uMode 0 (draw):  Porter-Duff "over" blend
 *  - uMode 1 (erase): Porter-Duff "destination-out" (subtract stroke mask)
 *
 *  UV transform handles layer rotation (translateX as Y-axis sphere rotation)
 *  and translateY UV shift in both display (R⁻¹ + −ty) and bake (+ty then R) modes.
 *
 *  uBakeMode:
 *  - 0: display — inverse rotation then −ty UV shift
 *  - 1: bake (+ty then R), source is premultiplied alpha (live brush/envelope RT)
 *  - 2: bake (+ty then R), source is straight alpha (world-UV display RT, for merge)
 */

export const compositeVertexShader = /*glsl*/`
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position, 1.0);
  }
`;

export const compositeFragmentShader = /*glsl*/`
  precision highp float;
  varying vec2 vUv;
  uniform sampler2D uBase;
  uniform sampler2D uStroke;
  uniform float uOpacity;
  uniform int uMode;
  uniform mat3 uRotInv;
  uniform float uTranslateY;
  uniform int   uBakeMode;

${uvToSphereGlsl}${sphereToUvGlsl}
  // Transform a UV coordinate for layer-stroke sampling.
  //
  // Display mode (uBakeMode==0, uRotInv = R_combined⁻¹):
  //   Undo rotation first, then subtract ty in UV space so the texture
  //   slides cleanly without pole-spinning.
  //
  // Bake mode (uBakeMode>0, uRotInv = R_combined forward):
  //   Add ty in UV space first (layer-local → world direction), then
  //   apply the forward rotation to reach world UV for stroke sampling.
  //   uBakeMode==1: source is premultiplied alpha (live brush/envelope RT)
  //   uBakeMode==2: source is straight alpha (world-UV display RT, used for merge)
  vec2 transformedStrokeUV(vec2 uv) {
    if (uBakeMode > 0) {
      // Bake: ty shift in layer-local space BEFORE forward rotation.
      vec3 p = uvToSphere(vec2(uv.x, clamp(uv.y + uTranslateY, 0.0, 1.0)));
      p = uRotInv * p;
      return sphereToUV(p);
    } else {
      // Display: inverse rotation THEN ty shift in UV space.
      vec3 p = uvToSphere(uv);
      p = uRotInv * p;
      vec2 r = sphereToUV(p);
      return vec2(r.x, clamp(r.y - uTranslateY, 0.0, 1.0));
    }
  }

  void main() {
    vec4 base   = texture(uBase,   vUv);
    vec4 stroke = texture(uStroke, transformedStrokeUV(vUv));

    if (uMode == 1) {
      // Erase: Porter-Duff destination-out — subtract stroke mask from base alpha.
      float eraseA = stroke.a * uOpacity;
      float outA = base.a * (1.0 - eraseA);
      gl_FragColor = vec4(base.rgb, outA);
    } else {
      // Draw: Porter-Duff over.
      float strokeA = stroke.a * uOpacity;
      float outA = strokeA + base.a * (1.0 - strokeA);
      // uBakeMode==1: stroke RT stores premultiplied alpha (brush/envelope shaders).
      //   contribution = stroke.rgb * uOpacity  (premult already contains stroke.a)
      // uBakeMode==0 or 2: stroke texture stores straight alpha (layer/display RTs).
      //   contribution = stroke.rgb * strokeA   (standard Porter-Duff)
      vec3 strokeContrib = uBakeMode == 1
        ? stroke.rgb * uOpacity
        : stroke.rgb * strokeA;
      vec3 outRGB = outA > 0.0
        ? (strokeContrib + base.rgb * base.a * (1.0 - strokeA)) / outA
        : vec3(0.0);
      gl_FragColor = vec4(outRGB, outA);
    }
  }
`;
